import type {
  VerifyCustomerPreviewCandidate,
  VerifyCustomerPreviewPayload,
  VerifyCustomerRole,
} from "../contract"
import {
  PROPERTY_CUSTOMER_ROLES,
  type PropertyCustomerRole,
} from "../../../../../../../../../../../../../_shared/_logic/enums"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} muss ein Objekt sein.`)
  }

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const stringValue = (value: unknown, path: string): string => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const nullableString = (value: unknown, path: string): string => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const roleValue = (value: unknown, path: string): VerifyCustomerRole => {
  const candidate = nullableString(value, path).toUpperCase()

  if (!candidate) return ""

  if (!PROPERTY_CUSTOMER_ROLES.includes(candidate as PropertyCustomerRole)) {
    throw new Error(`${path} enthält eine unbekannte Ansprechpartnerrolle.`)
  }

  return candidate as VerifyCustomerRole
}

const candidateValue = (value: unknown, path: string): VerifyCustomerPreviewCandidate => {
  const candidate = objectValue(value, path)
  exactKeys(candidate, ["customer_id", "display_name", "email", "phone"], path)

  return {
    customerId: stringValue(candidate.customer_id, `${path}.customer_id`),
    displayName: stringValue(candidate.display_name, `${path}.display_name`),
    email: nullableString(candidate.email, `${path}.email`),
    phone: nullableString(candidate.phone, `${path}.phone`),
  }
}

const candidateRolesValue = (value: unknown, path: string): Record<string, VerifyCustomerRole> => {
  const roles = objectValue(value, path)

  return Object.fromEntries(
    Object.entries(roles).map(([customerId, role]) => {
      if (!customerId.trim() || customerId.trim() !== customerId) {
        throw new Error(`${path} enthält eine ungültige Kunden-ID.`)
      }

      return [customerId, roleValue(role, `${path}.${customerId}`)]
    }),
  )
}

export const parseVerifyCustomerPreview = (payload: unknown): VerifyCustomerPreviewPayload => {
  const root = objectValue(payload, "payload")
  exactKeys(
    root,
    ["row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )

  const rawRows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const rawErrors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(shared, ["customer_candidates_by_id"], "payload.shared")
  const rawCandidates = objectValue(
    shared.customer_candidates_by_id,
    "payload.shared.customer_candidates_by_id",
  )

  const customerCandidatesById = Object.fromEntries(
    Object.entries(rawCandidates).map(([customerId, value]) => {
      const candidate = candidateValue(
        value,
        `payload.shared.customer_candidates_by_id.${customerId}`,
      )

      if (candidate.customerId !== customerId) {
        throw new Error(`Kandidat ${customerId} referenziert eine abweichende customer_id.`)
      }

      return [customerId, candidate]
    }),
  )

  const rowContextByActionCaseId = Object.fromEntries(
    Object.entries(rawRows).map(([actionCaseId, value]) => {
      const row = objectValue(value, `payload.row_context_by_action_case_id.${actionCaseId}`)
      exactKeys(
        row,
        ["candidate_customer_ids", "candidate_roles_by_customer_id"],
        `payload.row_context_by_action_case_id.${actionCaseId}`,
      )

      if (!Array.isArray(row.candidate_customer_ids)) {
        throw new Error(
          `payload.row_context_by_action_case_id.${actionCaseId}.candidate_customer_ids muss ein Array sein.`,
        )
      }

      return [
        actionCaseId,
        {
          candidateCustomerIds: row.candidate_customer_ids.map((id, index) =>
            stringValue(
              id,
              `payload.row_context_by_action_case_id.${actionCaseId}.candidate_customer_ids.${index}`,
            ),
          ),
          candidateRolesByCustomerId: candidateRolesValue(
            row.candidate_roles_by_customer_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.candidate_roles_by_customer_id`,
          ),
        },
      ]
    }),
  )

  const rowErrorsByActionCaseId = Object.fromEntries(
    Object.entries(rawErrors).map(([actionCaseId, value]) => [
      actionCaseId,
      stringValue(value, `payload.row_errors_by_action_case_id.${actionCaseId}`),
    ]),
  )

  for (const [actionCaseId, row] of Object.entries(rowContextByActionCaseId)) {
    for (const customerId of row.candidateCustomerIds) {
      if (!customerCandidatesById[customerId]) {
        throw new Error(
          `ActionCase ${actionCaseId} referenziert den unbekannten Kandidaten ${customerId}.`,
        )
      }

      if (!Object.hasOwn(row.candidateRolesByCustomerId, customerId)) {
        throw new Error(
          `ActionCase ${actionCaseId} enthält keine Rollen-Zuordnung für Kandidat ${customerId}.`,
        )
      }
    }
    for (const customerId of Object.keys(row.candidateRolesByCustomerId)) {
      if (!row.candidateCustomerIds.includes(customerId)) {
        throw new Error(
          `ActionCase ${actionCaseId} enthält eine Rollen-Zuordnung für den nicht referenzierten Kandidaten ${customerId}.`,
        )
      }
    }
  }

  return {
    rowContextByActionCaseId,
    rowErrorsByActionCaseId,
    customerCandidatesById,
  }
}
